import React, { useMemo, useState } from "react";
import { dateKey } from "../../utils/dashboardUtils";

const CHART_DIMENSIONS = {
  width: 760,
  height: 330,
  left: 54,
  right: 20,
  top: 28,
  bottom: 48,
};

const PERIOD_OPTIONS = [
  { value: "day", label: "Day" },
  { value: "week", label: "Weeks" },
  { value: "month", label: "Month" },
  { value: "year", label: "Year" },
];

const getDateFromVisit = (visit) => {
  const key = dateKey(visit.AppointmentDate || visit.BookingDate);
  return key ? new Date(`${key}T00:00:00`) : null;
};

const toLocalDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getWeekStart = (date) => {
  const weekStart = new Date(date);
  weekStart.setDate(date.getDate() - ((date.getDay() + 6) % 7));
  return weekStart;
};

const getFinancialYearStart = (date) =>
  date.getMonth() >= 3 ? date.getFullYear() : date.getFullYear() - 1;

const getPeriodDetails = (date, period) => {
  if (period === "day") {
    return {
      key: toLocalDateKey(date),
      label: date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
      }),
    };
  }

  if (period === "week") {
    const weekStart = getWeekStart(date);
    return {
      key: toLocalDateKey(weekStart),
      label: weekStart.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
      }),
    };
  }

  if (period === "year") {
    const financialYearStart = getFinancialYearStart(date);
    return {
      key: String(financialYearStart),
      label: `FY ${financialYearStart}-${String(financialYearStart + 1).slice(-2)}`,
    };
  }

  return {
    key: toLocalDateKey(date).slice(0, 7),
    label: date.toLocaleDateString("en-IN", {
      month: "short",
      year: "numeric",
    }),
  };
};

const getPeriodDates = (period, today = new Date()) => {
  const currentDate = new Date(today);
  currentDate.setHours(0, 0, 0, 0);

  if (period === "day") {
    return Array.from({ length: 30 }, (_, index) => {
      const date = new Date(currentDate);
      date.setDate(currentDate.getDate() - index);
      return date;
    });
  }

  if (period === "week") {
    const currentWeekStart = getWeekStart(currentDate);
    return Array.from({ length: 12 }, (_, index) => {
      const date = new Date(currentWeekStart);
      date.setDate(currentWeekStart.getDate() - index * 7);
      return date;
    });
  }

  if (period === "year") {
    const currentFinancialYearStart = getFinancialYearStart(currentDate);
    return Array.from(
      { length: 3 },
      (_, index) => new Date(currentFinancialYearStart - index, 3, 1),
    );
  }

  return Array.from({ length: 12 }, (_, index) =>
    new Date(currentDate.getFullYear(), currentDate.getMonth() - index, 1),
  );
};

const getChartMaximum = (maximumValue) => {
  if (maximumValue <= 5) {
    return 5;
  }

  const magnitude = 10 ** Math.floor(Math.log10(maximumValue));
  const normalized = maximumValue / magnitude;
  const step =
    normalized <= 2
      ? magnitude / 5
      : normalized <= 5
        ? magnitude / 2
        : magnitude;

  return Math.ceil(maximumValue / step) * step;
};

const OpdServedChart = ({ opdVisits }) => {
  const [chartType, setChartType] = useState("bar");
  const [chartPeriod, setChartPeriod] = useState("month");

  const chartData = useMemo(() => {
    const rows = getPeriodDates(chartPeriod).map((date) => ({
      ...getPeriodDetails(date, chartPeriod),
      totalOpd: 0,
    }));
    const countsByPeriod = rows.reduce((periodMap, row) => {
      periodMap[row.key] = row;
      return periodMap;
    }, {});

    opdVisits.forEach((visit) => {
      const visitDate = getDateFromVisit(visit);

      if (!visitDate) {
        return;
      }

      const { key } = getPeriodDetails(visitDate, chartPeriod);
      if (countsByPeriod[key]) {
        countsByPeriod[key].totalOpd += 1;
      }
    });

    const maximumValue = Math.max(...rows.map((row) => row.totalOpd), 0);

    return {
      rows,
      maximumValue: getChartMaximum(maximumValue),
    };
  }, [opdVisits, chartPeriod]);

  const chartWidth =
    CHART_DIMENSIONS.width - CHART_DIMENSIONS.left - CHART_DIMENSIONS.right;
  const chartHeight =
    CHART_DIMENSIONS.height - CHART_DIMENSIONS.top - CHART_DIMENSIONS.bottom;
  const chartBottom = CHART_DIMENSIONS.top + chartHeight;
  const xStep = chartWidth / Math.max(chartData.rows.length, 1);
  const points = chartData.rows.map((row, index) => ({
    ...row,
    x: CHART_DIMENSIONS.left + xStep * (index + 0.5),
    y: chartBottom - (row.totalOpd / chartData.maximumValue) * chartHeight,
  }));
  const linePath = points
    .map((point, index) => `${index ? "L" : "M"} ${point.x} ${point.y}`)
    .join(" ");
  const areaPath = points.length
    ? `${linePath} L ${points[points.length - 1].x} ${chartBottom} L ${points[0].x} ${chartBottom} Z`
    : "";
  const barWidth = Math.min(54, Math.max(14, xStep * 0.62));
  const yTicks = Array.from({ length: 6 }, (_, index) => {
    const value = (chartData.maximumValue / 5) * index;
    return {
      value: Number(value.toFixed(2)),
      y: chartBottom - (value / chartData.maximumValue) * chartHeight,
    };
  });

  return (
    <section
      className="dashboard-table-wrap opd-chart-report"
      aria-labelledby="opd-chart-title"
    >
      <div className="opd-chart-header">
        <div className="opd-chart-title-row">
          <svg
            className="opd-chart-title-icon"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path d="M3 12h4l2-5 3 10 2-5h7" />
            <circle cx="12" cy="12" r="9" />
          </svg>
          <h2 id="opd-chart-title">OPD Served</h2>
        </div>
      </div>
      <div className="opd-chart-header opd-chart-header--controls">
        <div className="opd-chart-type-controls" aria-label="Select chart type">
          {["bar", "line", "area"].map((type) => (
            <button
              key={type}
              type="button"
              className={`opd-chart-type-button ${type} ${chartType === type ? "is-active" : ""}`}
              onClick={() => setChartType(type)}
              aria-pressed={chartType === type}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>
      </div>
      <div
        className="opd-chart-period-controls"
        aria-label="Select OPD time grouping"
      >
        {PERIOD_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            className={`opd-chart-period-button ${chartPeriod === option.value ? "is-active" : ""}`}
            onClick={() => setChartPeriod(option.value)}
            aria-pressed={chartPeriod === option.value}
          >
            {option.label}
          </button>
        ))}
      </div>

      {chartData.rows.length === 0 ? (
        <div className="empty-panel">No OPD records available</div>
      ) : (
        <div className="opd-chart-canvas">
          <svg
            className="opd-chart-svg"
            viewBox={`0 0 ${CHART_DIMENSIONS.width} ${CHART_DIMENSIONS.height}`}
            role="img"
            aria-label={`${chartPeriod} wise OPD ${chartType} chart`}
          >
            <defs>
              <linearGradient
                id="opd-chart-area-fill"
                x1="0"
                x2="0"
                y1="0"
                y2="1"
              >
                <stop offset="0%" stopColor="#fa1bc7" stopOpacity="0.5" />
                <stop offset="100%" stopColor="#fa1bc7" stopOpacity="0.05" />
              </linearGradient>
            </defs>

            {yTicks.map((tick) => (
              <g key={tick.value}>
                <line
                  className="opd-chart-grid-line"
                  x1={CHART_DIMENSIONS.left}
                  x2={CHART_DIMENSIONS.width - CHART_DIMENSIONS.right}
                  y1={tick.y}
                  y2={tick.y}
                />
                <text
                  className="opd-chart-y-label"
                  x={CHART_DIMENSIONS.left - 10}
                  y={tick.y + 4}
                >
                  {tick.value}
                </text>
              </g>
            ))}

            {chartType === "area" && (
              <path className="opd-chart-area" d={areaPath} />
            )}

            {chartType === "bar" &&
              points.map((point) => (
                <g key={point.key}>
                  <rect
                    className="opd-chart-bar"
                    x={point.x - barWidth / 2}
                    y={point.y}
                    width={barWidth}
                    height={chartBottom - point.y}
                  />
                  <text
                    className="opd-chart-value-label"
                    x={point.x}
                    y={Math.max(point.y - 8, 16)}
                  >
                    {point.totalOpd}
                  </text>
                </g>
              ))}

            {chartType !== "bar" && (
              <>
                <path className="opd-chart-line" d={linePath} />
                {points.map((point) => (
                  <g key={point.key}>
                    <circle
                      className="opd-chart-point"
                      cx={point.x}
                      cy={point.y}
                      r="4"
                    />
                    <text
                      className="opd-chart-value-label"
                      x={point.x}
                      y={Math.max(point.y - 10, 16)}
                    >
                      {point.totalOpd}
                    </text>
                  </g>
                ))}
              </>
            )}

            {points.map((point) => (
              <text
                className="opd-chart-x-label"
                key={point.key}
                x={point.x}
                y={chartBottom + 25}
              >
                {point.label}
              </text>
            ))}
          </svg>
        </div>
      )}
    </section>
  );
};

export default OpdServedChart;
